/**
 * WebView 관련 유틸리티 함수들
 */

import { logger } from './logger'

// TypeScript 타입 정의
declare global {
  interface Window {
    ReactNativeWebView?: any
    ReactNativeBridge?: {
      sendMessage: (type: string, data: any) => void
      sendMessageAsync: (type: string, data: any) => Promise<any>
      onMessage: (callback: (message: any) => void) => void
    }
  }
}

// WebView 환경인지 확인
export const isWebView = (): boolean => {
  return typeof window !== 'undefined' && (!!window.ReactNativeWebView || !!window.ReactNativeBridge)
}

// React Native Bridge가 사용 가능한지 확인
export const isReactNativeBridgeAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!window.ReactNativeBridge
}

// WebView Bridge를 통한 메시지 전송 (Promise 기반)
export const sendWebViewMessage = async (type: string, data: any = {}): Promise<any> => {
  if (!isReactNativeBridgeAvailable() || !window.ReactNativeBridge) {
    logger.error('React Native Bridge가 사용 가능하지 않습니다.')
    throw new Error('React Native Bridge가 사용 가능하지 않습니다.')
  }

  // sendMessageAsync가 존재하는지 확인
  if (typeof window.ReactNativeBridge.sendMessageAsync !== 'function') {
    logger.error('sendMessageAsync is not a function. Available methods:', Object.keys(window.ReactNativeBridge))
    throw new Error('sendMessageAsync 메서드를 사용할 수 없습니다.')
  }

  try {
    logger.debug(`[WebView] 메시지 전송: ${type}`, data)

    // sendMessageAsync가 이미 자체 타임아웃을 처리하므로 추가 타임아웃 불필요
    const result = await window.ReactNativeBridge.sendMessageAsync(type, data)

    logger.debug(`[WebView] 메시지 응답 수신: ${type}`, result)
    return result
  } catch (error) {
    logger.error('WebView 메시지 전송 실패:', error)
    throw error
  }
}

// WebView에서 Google Sign-In 수행
export const signInWithGoogleNative = async (): Promise<{
  user: any
  idToken: string
  accessToken: string
  cancelled?: boolean
}> => {
  if (!isWebView()) {
    logger.error('WebView 환경이 아닙니다.')
    throw new Error('WebView 환경이 아닙니다.')
  }

  try {
    logger.auth('네이티브 Google Sign-In 시작')
    const result = await sendWebViewMessage('AUTH:GOOGLE_SIGN_IN', {})

    if (!result.success) {
      logger.error('Google Sign-In 실패:', result.error)
      throw new Error(result.error || 'Google Sign-In 실패')
    }

    logger.success('네이티브 Google Sign-In 성공')
    return result.data
  } catch (error) {
    logger.error('Native Google Sign-In 실패:', error)
    throw error
  }
}

// WebView에서 Google Sign-Out 수행
export const signOutWithGoogleNative = async (): Promise<void> => {
  if (!isWebView()) {
    logger.error('WebView 환경이 아닙니다.')
    throw new Error('WebView 환경이 아닙니다.')
  }

  try {
    logger.auth('네이티브 Google Sign-Out 시작')
    const result = await sendWebViewMessage('AUTH:GOOGLE_SIGN_OUT', {})

    if (!result.success) {
      logger.error('Google Sign-Out 실패:', result.error)
      throw new Error(result.error || 'Google Sign-Out 실패')
    }

    logger.success('네이티브 Google Sign-Out 성공')
  } catch (error) {
    logger.error('Native Google Sign-Out 실패:', error)
    throw error
  }
}

// WebView에서 Apple Sign-In 수행
export const signInWithAppleNative = async (): Promise<{
  user: string
  fullName: any
  email: string | null
  identityToken: string
  authorizationCode: string
  realUserStatus: number
  cancelled?: boolean
}> => {
  if (!isWebView()) {
    logger.error('WebView 환경이 아닙니다.')
    throw new Error('WebView 환경이 아닙니다.')
  }

  try {
    logger.auth('네이티브 Apple Sign-In 시작')
    const result = await sendWebViewMessage('AUTH:APPLE_SIGN_IN', {})

    if (!result.success) {
      logger.error('Apple Sign-In 실패:', result.error)
      throw new Error(result.error || 'Apple Sign-In 실패')
    }

    logger.success('네이티브 Apple Sign-In 성공')
    return result.data
  } catch (error) {
    logger.error('Native Apple Sign-In 실패:', error)
    throw error
  }
}

// WebView에서 Apple Sign-Out 수행
export const signOutWithAppleNative = async (): Promise<void> => {
  if (!isWebView()) {
    logger.error('WebView 환경이 아닙니다.')
    throw new Error('WebView 환경이 아닙니다.')
  }

  try {
    logger.auth('네이티브 Apple Sign-Out 시작')
    const result = await sendWebViewMessage('AUTH:APPLE_SIGN_OUT', {})

    if (!result.success) {
      logger.error('Apple Sign-Out 실패:', result.error)
      throw new Error(result.error || 'Apple Sign-Out 실패')
    }

    logger.success('네이티브 Apple Sign-Out 성공')
  } catch (error) {
    logger.error('Native Apple Sign-Out 실패:', error)
    throw error
  }
}

// iOS 플랫폼 감지 (WebView 환경에서)
export const isIOSWebView = (): boolean => {
  return isWebView() && /iPad|iPhone|iPod/.test(navigator.userAgent)
}

// Android 플랫폼 감지 (WebView 환경에서)
export const isAndroidWebView = (): boolean => {
  return isWebView() && /Android/.test(navigator.userAgent)
}

// iOS 플랫폼 감지 (일반 브라우저 포함)
export const isIOS = (): boolean => {
  // MSStream은 구버전 Edge 브라우저 체크용 - 타입 안전성을 위해 in 연산자 사용
  const hasOldEdge = 'MSStream' in window
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !hasOldEdge
}

// Android 플랫폼 감지 (일반 브라우저 포함)
export const isAndroid = (): boolean => {
  return /Android/.test(navigator.userAgent)
}
